import React, { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import Figure from '../molecules/Figure';
import clsx from 'clsx';
import Button from '../atoms/Button';
import Img from '../atoms/Img';
import {
  imageTypes,
  defaultImageExt,
  defaultDocExt,
  mimeTypesByExt,
} from '../../data/constants';
import { sizeToString, convertBytes } from '../../lib/apis/file';
import styles from '../../styles/components/molecules/Upload.module.scss';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { LexoRank } from 'lexorank';

interface UploadProps {
  name: string;
  className?: string;
  bg?: boolean;
  type?: string;
  label?: string;
  half?: boolean;
  full?: boolean;
  bigThumb?: boolean;
  float?: boolean;
  optional?: boolean;
  multiple?: boolean;
  loading?: boolean;
  error?: boolean;
  dragArea?: boolean;
  contain?: boolean;
  hideInfo?: boolean;
  hideError?: boolean;
  maxFiles?: number;
  defaultValue?: any;
  maxFileSizeInBytes?: any;
  formats?: string[];
  data?: [];
  onError?: (error: string) => void;
  onChange?: (files: File[]) => void;
  onChangeOrder?: (files: File[]) => void;
  onRemove?: (files: File[]) => void;
}

export default function Upload(props: UploadProps) {
  const { t } = useTranslation(['common']);
  const [hover, setHover] = useState(false);
  const [currError, setCurrError] = useState(false);
  const [files, setFiles] = useState([] as any);
  const [isBrowser, setIsBrowser] = useState(false);
  const {
    name,
    className,
    bg,
    half,
    full,
    bigThumb,
    float,
    type,
    label,
    optional,
    loading,
    error,
    contain,
    multiple,
    dragArea,
    data,
    formats,
    maxFileSizeInBytes,
    maxFiles,
    onError,
    onChange,
    onChangeOrder,
    onRemove,
    hideInfo,
    hideError,
  } = props;
  const maxCnt = maxFiles || 10;
  const maxSize =
    maxFileSizeInBytes || (props.type == 'image' ? 5000000 : 10000000000); // 5MB / 10GB
  const maxMB = convertBytes(maxSize, 'MB');
  const hasFiles = !!files.length;
  const inputID = name || `file-input`;
  const currFormats =
    formats || (props.type == 'image' ? defaultImageExt : defaultDocExt);
  const btnLabel = hasFiles
    ? ''
    : !!label
    ? label
    : type === 'image'
    ? multiple
      ? t(`common:cta-select-images`)
      : t(`common:cta-select-image`)
    : multiple
    ? t(`common:cta-select-files`)
    : t(`common:cta-select-file`);
  const dragLabel = !dragArea
    ? ''
    : type === 'image'
    ? multiple
      ? t(`common:drag-images`)
      : t(`common:drag-image`)
    : multiple
    ? t(`common:drag-files`)
    : t(`common:drag-file`);
  const info = t('common:upload-info', {
    size: `${maxMB} MB`,
    formats: currFormats.join(', '),
  });
  const classes = clsx(
    styles.upload,
    { [styles.bg]: dragArea && bg },
    { [styles.half]: !full && half },
    { [styles.full]: !half && full },
    { [styles.bigThumb]: bigThumb },
    { [styles.contain]: contain },
    { [styles.error]: !!currError },
    { [styles.hasFiles]: hasFiles },
    // { [styles.hasMaxFiles]: files.length >= maxCnt },
    { [styles.multiple]: multiple },
    { [styles.dragArea]: dragArea },
    { [styles.loading]: loading },
    { [styles.float]: float },
    className
  );

  const browseFiles = (e: any) => {
    const field = document.getElementById(inputID);
    if (field) {
      field.click();
    }
  };

  const addFiles = (e: any) => {
    e.preventDefault();
    const field = e.target; //document.getElementById(inputID) as HTMLInputElement;

    let highestRank = '0|0i0000:';
    if (files.length) {
      highestRank = files[files.length - 1].lexorank;
    }

    const newFiles = Array.from(field.files)
      .map((file: any, i: number) => {
        const { size, name, type } = file;
        const url = URL.createObjectURL(file);
        const fileType = type.split('/')[1] || type.split('/')[0];
        const display =
          imageTypes.indexOf(fileType) > -1 ? 'image' : 'document';
        const lexorank = LexoRank.parse(highestRank).genNext().toString();
        const fileObj = { name, size, type, url, display, file, lexorank };
        return fileObj;
      })
      .slice(0, 10);

    const oldFiles = [
      ...files.filter(
        (oldFile: any) => !newFiles.some((el: any) => oldFile.name === el.name)
      ),
    ];

    const mergedFiles = [...oldFiles, ...newFiles];

    const checkedFiles = mergedFiles.map((file: any, i: number) => {
      const { id, size, type } = file;
      const allowed = currFormats.some(
        (str: string) => type.indexOf(mimeTypesByExt[str]) > -1
      );
      const error =
        !id && !allowed
          ? t('common:error-fileFormat', {
              formats: ` ${currFormats.join(', ')}`,
            })
          : !id && size > maxSize
          ? t('common:error-maxSize', { max: `${maxMB} MB` })
          : i >= maxCnt
          ? t('common:error-maxFiles', { max: maxCnt })
          : '';
      return { ...file, error };
    });

    field.value = '';
    updateFiles(checkedFiles);
  };

  const removeFile = (file: any) => {
    const field = document.getElementById(inputID) as HTMLInputElement;
    field.value = '';
    const { name, id, image_id } = file;
    const reducedFiles = [
      ...files.filter((el: any) =>
        name
          ? el.name !== name
          : image_id
          ? el.image_id !== image_id
          : id
          ? el.id !== id
          : true
      ),
    ];
    updateFiles(reducedFiles);
    if (!!onRemove) {
      onRemove(file);
    }
  };

  const updateFiles = (currFiles: any) => {
    const newFiles = currFiles.filter((el: any) => !el.id);
    const errorFiles = newFiles.filter((el: any) => !!el.error);
    const validFiles = newFiles.filter((el: any) => !el.error).slice(0, maxCnt);
    const errorMsg = errorFiles
      .map((el: any) => el.error)
      .filter((v: any, i: any, a: any) => a.indexOf(v) === i) //make unique
      .join(', ');

    setCurrError(errorMsg);
    setFiles(currFiles);

    if (!!onError) {
      onError(
        !!errorMsg ? `${errorMsg}` : '' // t('common:error-invalidFiles')
      );
    }

    if (!!onChange) {
      onChange(validFiles.map((el: any) => el.file));
    }

    if (!!onChangeOrder) {
      onChangeOrder(currFiles);
    }
  };

  useEffect(() => {
    const currData = (Array.isArray(data) ? data : !!data ? [data] : []).map(
      (el: any) => {
        const fileType = el.type.split('/')[1] || el.type.split('/')[0];
        const display =
          imageTypes.indexOf(fileType) > -1 ? 'image' : 'document';
        return { ...el, display };
      }
    );
    setFiles(currData);
  }, [data]);

  useEffect(() => {
    setCurrError(!!error);
  }, [error]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsBrowser(true);
    }
  }, []);

  const onDragEnd = (result: any) => {
    const destinationIndex = result.destination?.index;
    const sourceIndex = result.source.index;

    if (destinationIndex === sourceIndex || destinationIndex === undefined) {
      return;
    }

    const newFiles = [...files];
    const [moved] = newFiles.splice(sourceIndex, 1);
    newFiles.splice(destinationIndex, 0, moved);

    const prevFile = newFiles[destinationIndex - 1];
    const prevLexorank = prevFile?.lexorank
      ? LexoRank.parse(prevFile.lexorank)
      : null;
    const nextFile = newFiles[destinationIndex + 1];
    const nextLexorank = nextFile?.lexorank
      ? LexoRank.parse(nextFile.lexorank)
      : null;

    let newRank = '0|0i0000:';

    if (prevLexorank && nextLexorank) {
      newRank = prevLexorank.between(nextLexorank).toString();
    } else if (prevLexorank) {
      newRank = prevLexorank.genNext().toString();
    } else if (nextLexorank) {
      newRank = nextLexorank.genPrev().toString();
    }
    moved.lexorank = newRank;

    updateFiles(newFiles);
  };

  return (
    <div className={classes}>
      <div
        onMouseOver={dragArea ? () => setHover(true) : undefined}
        onMouseOut={dragArea ? () => setHover(false) : undefined}
      >
        {!!dragLabel && !error && (
          <label htmlFor={inputID}>
            {dragLabel}
            <br />
            {info}
          </label>
        )}
        <input
          type="file"
          name={name || inputID}
          id={inputID}
          onChange={addFiles}
          multiple={multiple}
          required={!optional}
        />

        <DragDropContext onDragEnd={onDragEnd}>
          {isBrowser ? (
            <Droppable droppableId="strength" direction="horizontal">
              {(provided) => (
                <ul
                  className="characters"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {files.map((file: any, i: number) => {
                    const { size, name, small, url, error, display } = file;
                    const sizeStr = `(${sizeToString(size)})`;
                    const liClasses = clsx(
                      { [styles.errorFile]: !!error },
                      { [styles.document]: display !== 'image' }
                    );
                    return (
                      <Draggable
                        draggableId={`drag-upload-${i}`}
                        index={i}
                        key={i}
                        isDragDisabled={files.length < 2}
                      >
                        {(provided, snapshot) => (
                          <li
                            ref={provided.innerRef}
                            className={liClasses}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            {display === 'image' ? (
                              <Img
                                src={url || small}
                                contain={contain}
                                height={contain ? undefined : 100}
                                width={contain ? undefined : 100}
                              />
                            ) : (
                              <Figure
                                tiny
                                icon={'document'}
                                title={name}
                                caption={
                                  <>
                                    <span>{sizeStr}</span>
                                  </>
                                }
                              />
                            )}
                            <Button
                              white
                              icon="remove"
                              onClick={() => removeFile(file)}
                            />
                          </li>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                  <li>
                    <Button
                      dark={!error}
                      red={!!error}
                      icon="plus"
                      large={!hasFiles}
                      iconLarge={!hasFiles}
                      fill={hasFiles}
                      label={btnLabel}
                      hover={hover}
                      onClick={browseFiles}
                    />
                  </li>
                </ul>
              )}
            </Droppable>
          ) : null}
        </DragDropContext>
        {!hideInfo && !dragLabel && <small>{info}</small>}
      </div>

      {!!currError && !hideError && <small>{currError}</small>}
    </div>
  );
}
